import { useDispatch, useSelector } from "react-redux";
import { useContext, useEffect, useState } from "react";
import { ParentScreenCtx } from "../../pages/workbench_build/WorkbenchBuild";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { PDFDocument } from "pdf-lib";
import { treeActions } from "../../redux/slices/treeSlice";
import InValidJSONResponse from "./InvalidJSONResponse";
import { api } from "../../redux/api";

const typePath = {
	inbound: "inbound_templates/",
	outbound: "outbound_templates/",
	workflow: "",
};

export default function DocPanel() {
	const parentScreen = useContext(ParentScreenCtx);
	const { account, user, baseUrl, token } = useSelector((state) => state.queryParams);
	const { leftTree } = useSelector((state) => state.treeData[parentScreen]);
	const { selectedNode } = leftTree.tree.body;
	const [isLoading, setIsLoading] = useState(false);
	const [htmlContent, setHtmlContent] = useState("");
	const [pdfUrl, setPdfUrl] = useState(null);
	const [imageSrc, setImageSrc] = useState(null)
	const [error, setError] = useState(null);
	const [showPrompt, setShowPrompt] = useState(false);
	const [wasPromptShown, setWasPromptShown] = useState(false);

	const { t } = useTranslation();
	const dispatch = useDispatch();
    
	const getTemplateData = async () => {
		const templateData = parentScreen === "inbound" ? "getInboundTemplateData" : parentScreen === "outbound" ? "getOutboundTemplateData" : "getWorkflowData";
		const workflowGroupName = parentScreen === "workflow" ? "workflow_group_name" : "workflowGroupName";
		const templateName = parentScreen === "workflow" ? "workflow_name" : "templateName";
		const template =  await dispatch(api.endpoints[templateData].initiate({
			account,
			[workflowGroupName]: encodeURIComponent(selectedNode?.workflowGroup),
			[templateName]: selectedNode?.title,
		},{forceRefetch: true}))
		if(template?.data){
			return template.data;
		}
	}
    const getDocument = async () => {
		setIsLoading(true);
		setError(null);
		const workflow = selectedNode?.workflowGroup || "";
		const title = selectedNode?.title || "";
		const ext = selectedNode?.ext?.toLowerCase();
		const relativePath = `${account}/${workflow}/${typePath[parentScreen]}${title}`;
		const url = `${baseUrl}/workbench?action=get_template_file&user=${user}&account=${account}&relative_path=${relativePath}`;
		try {
           
			const response = await axios.get(url, {
				responseType: ext === ".pdf" ? "arraybuffer" : "text",
				headers: { "Authorization": `Bearer ${token}` }
			});
	        if(ext === "json"){
				setHtmlContent("");
				setPdfUrl(null);
				setImageSrc(null);
				setShowPrompt(true);
				setWasPromptShown(true);
				const data = await getTemplateData();
			    if (data) dispatch(
					treeActions.setDocFileData({
					blob: data,
					fileName: selectedNode?.title,
					screen: parentScreen,
					})
			 );
			}
			else if (ext === "pdf" ) {
				const pdfDoc = await PDFDocument.load(response.data);
				const base64String = await pdfDoc.saveAsBase64({ dataUri: true });
				setPdfUrl(base64String);
				setHtmlContent("");
				dispatch(treeActions.setDocFileData({
					blob: base64String,
					fileName: title,
					screen: parentScreen,
				  })
				);
			} else if (ext === "png" || ext === "jpg" || ext === "jpeg" || ext === "gif") {
				
				setPdfUrl("");
				setHtmlContent("");	
				setImageSrc(response.data)
				dispatch(treeActions.setDocFileData({
					blob: response.data,
					fileName: title,
					screen: parentScreen,
				  })
				);

			}else {
				setHtmlContent(response.data);
				setPdfUrl(null);
				setImageSrc(null)
				dispatch(treeActions.setDocFileData({
					blob: response.data,
					fileName: title,
					screen: parentScreen,
				  })
				);
			}
		} catch (err) {
			console.error("Error fetching document:", err.message);
			setError(`${t("ERROR_LOADING_FILE")}: ${err.message}`);
			setHtmlContent("");
			setPdfUrl(null);
			dispatch(treeActions.setDocFileData({
				blob: null,
				fileName: '',
				screen: parentScreen,
			  })
			)
		} finally {
			setIsLoading(false);
		}
	};

	// Cleanup Blob URL
	useEffect(() => {
		return () => {
			if (pdfUrl) {
				URL.revokeObjectURL(pdfUrl);
			}
		};
	}, [pdfUrl]);
    
	useEffect(() => {
		return () => {
			if(imageSrc){
				URL.revokeObjectURL(imageSrc)
			}
		}
	},[imageSrc])

	useEffect(() => {
		if (selectedNode?.key) {
			getDocument();
		}
	}, [selectedNode?.key]);

	return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}>
      {isLoading ? (
        <CircularProgress />
      ) : error ? (
        <div>{error}</div>
      ) : pdfUrl ? (
        <iframe
          src={pdfUrl}
          style={{ width: "100%", height: "95%", border: "none" }}
          title="PDF Preview"
          onError={() => setError(t("ERROR_RENDERING_PDF"))}
        />
      ) : htmlContent ? (
        <iframe
          srcDoc={`<style>html, body { height: 98%; margin: 0; padding: 0; }</style>${htmlContent}`}
          style={{ width: "100%", height: "100%", border: "none" }}
          title="Document Preview"
          onError={(e) => {
            console.error("HTM DOC ERROR:", e);
          }}
        />
      ) : imageSrc ? (
        <img
          src={imageSrc}
          style={{
            maxWidth: "100%",
            maxHeight: "95vh",
            objectFit: "contain",
            border: "none",
          }}
          alt="Document Preview"
          onError={(e) => {
            console.error("Image loading error:", e);
            setError(t("ERROR_LOADING_IMAGE"));
          }}
        />
      ) : showPrompt ? (
        <InValidJSONResponse
          open={showPrompt}
          handleClose={(val) => setShowPrompt(val)}
          fileName={selectedNode?.title}
        />
      ) : wasPromptShown ? (
		<></> 
	  ) : (
        <div>{t("SOMETHING_WENT_WRONG")}</div>
      )}
    </div>
  );
}